import React from "react";
import { observer } from "mobx-react-lite";
import { Typography, Info, SvgIcon, Button, Input, clsx } from "@gemlightbox/core-kit";

import { Webgl2New } from "src/image-editor-core";
import { useStores } from "src/hooks";
import { focusAndScrollToCenter } from "src/utils";
import { MeasurementTutorial } from "../../../measurement-tutorial";
import { editMediaStore } from "../../../edit-media.store";
import { GroupBlock } from "../../group-block";
import { AddColorButton } from "./add-color-button";
import { MeasurementColor } from "./measurement-color";

import { ReactComponent as TrashSVG } from "src/external-ts/assets/images/trash-grey.svg";
import styles from "./measurement-item.module.css";

export type MeasurementItemProps = {
  index: number;
  measurement: Webgl2New.MeasurementComponent;
  className?: string;
  isReal?: boolean;
  disableAutoFocus?: boolean;
  textValue?: string;
  onMeasurementChange?: (measurement: Webgl2New.MeasurementComponent, value: string) => void;
  onRemoveMeasurement?: (measurement: Webgl2New.MeasurementComponent) => void;
};

export const MeasurementItem: React.FC<MeasurementItemProps> = observer(
  ({
    index,
    measurement,
    className = "",
    isReal = false,
    disableAutoFocus = false,
    textValue,
    onMeasurementChange,
    onRemoveMeasurement,
  }: MeasurementItemProps) => {
    const { localeStore } = useStores();

    const { renderer, measurementColors } = editMediaStore;

    const isSelected = measurement === editMediaStore.currentMeasurement;
    const measurementText = textValue ?? measurement.text;
    const [inputValue, setInputValue] = React.useState(measurementText);
    const inputRef = React.useRef<(HTMLInputElement & HTMLTextAreaElement) | null>(null);

    React.useEffect(() => {
      setInputValue(measurementText);
    }, [measurement, measurementText]);

    React.useEffect(() => {
      if (!isSelected) return;
      if (disableAutoFocus) return;

      if (!inputRef.current) return;
      focusAndScrollToCenter(inputRef.current, { behavior: "smooth" });
    }, [disableAutoFocus, isSelected]);

    const handleSelectMeasureItemClick = (e: React.MouseEvent<HTMLElement>) => {
      if (e.currentTarget !== e.target) return;

      editMediaStore.currentMeasurement = isSelected
        ? editMediaStore.fantomMeasurement
        : measurement;
    };

    const handleChangeText = (value: string) => {
      setInputValue(value);
      if (onMeasurementChange) {
        onMeasurementChange(measurement, value);
      } else {
        measurement.changeText(value);
        renderer.render();
      }
    };

    const handleRemoveMeasurement = () => onRemoveMeasurement?.(measurement);

    return (
      <GroupBlock
        className={clsx(styles.measurementItemBlockGroup, className)}
        onClick={handleSelectMeasureItemClick}
        selected={isSelected}
      >
        {isReal && (
          <Button
            className={styles.trashBtn}
            appearance="errorGhost"
            onClick={handleRemoveMeasurement}
          >
            <SvgIcon icon={TrashSVG} />
          </Button>
        )}

        <div className={styles.title} onClick={handleSelectMeasureItemClick}>
          <Typography size="small600" onClick={handleSelectMeasureItemClick}>
            {localeStore.t('["edit-media"].inspector.measurements["measurement-item"].title')}&nbsp;
            {index || ""}
          </Typography>
          {!index && (
            <Info position="right" size={16} withAngle>
              <MeasurementTutorial />
            </Info>
          )}
        </div>

        <Input
          className={styles.measurementInput}
          appearance="primaryV2"
          placeholder={localeStore.t(
            '["edit-media"].inspector.measurements["measurement-item"].placeholder',
          )}
          maxLength={50}
          value={inputValue}
          forwardRef={inputRef}
          onChange={handleChangeText}
          disableError
        />

        {(isSelected || !isReal) && (
          <div className={styles.colorsContainer}>
            <div className={styles.colorsTitle}>
              <Typography size="extraSmall">
                {localeStore.t(
                  '["edit-media"].inspector.measurements["measurement-item"]["line-color"]',
                )}
              </Typography>
              <Typography size="extraSmall" color="textTertiary">
                {localeStore.t('["edit-media"]["max-10"]')}
              </Typography>
            </div>
            <div className={styles.colorsWrapper}>
              <AddColorButton measurement={measurement} />

              {measurementColors.map((color, index) => (
                <MeasurementColor
                  key={index}
                  color={color}
                  index={index}
                  measurement={measurement}
                />
              ))}
            </div>
          </div>
        )}
      </GroupBlock>
    );
  },
);

export default MeasurementItem;
